<template>
  <modal
    :header-text="$t('modal.addNewUser')"
    :button-text="$t('global.add')"
    :class="$style.modal"
    @action="addUser()"
    @close="$emit('close')"
  >
    <div :class="$style.alert">
      {{ alert }}
    </div>
    <div :class="$style.inputContainer">
      <label :class="$style.label">{{ $t('global.login') }}</label>
      <input v-model="login" type="text" :class="$style.input">
    </div>

    <div :class="$style.inputContainer">
      <label :class="$style.label">{{ $t('global.password') }}</label>
      <input v-model="password" type="password" :class="$style.input">
    </div>

    <div :class="$style.inputContainer">
      <label :class="$style.label"> Email</label>
      <input v-model="email" type="text" :class="$style.input" @blur="validate()">
    </div>
    <div :class="$style.titleRight">
      {{ $t('settings.copyControllerPermissions') }}
    </div>
    <div :class="$style.userContainer">
      <div :class="$style.inputContainer">
        <div :class="$style.customCheckBoxContainer" @click="toggleRights()">
          <input v-model="copyRights" type="checkbox" :class="$style.customCheckbox">
          <label>
            <check-mark />
            <div :class="$style.textContainer">{{ $t('settings.copyRights') }}</div>
          </label>
        </div>
        <vue-select
          v-model="selectedLogin"
          label="login"
          :options="controls"
          :class="$style.select"
          :clearable="false"
          :disabled="!copyRights"
          @input="setSelectedUser"
        />
      </div>
      <div :class="{ [$style.disabledContainer]: !copyRights }">
        <div :class="[$style.inputContainer, $style.inputContainerCheckbox]">
          <div :class="$style.customCheckBoxContainer" @click="toggleData()">
            <input v-model="availableData" type="checkbox" :class="$style.customCheckbox">
            <label>
              <check-mark />
              <div :class="$style.textContainer">{{ $t('settings.availableData') }}</div>
            </label>
          </div>
        </div>
        <div :class="[$style.inputContainer, $style.inputContainerCheckbox]">
          <div :class="$style.customCheckBoxContainer" @click="toggleUsers()">
            <input v-model="availableUsers" type="checkbox" :class="$style.customCheckbox">
            <label>
              <check-mark />
              <div :class="$style.textContainer">{{ $t('settings.availableUsers') }}</div>
            </label>
          </div>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import Vue from 'vue';
import VueToast from 'vue-toast-notification';
import { addControl } from '@/api/methods/control';
import Modal from '@/components/common/modal/control-modal.vue';
import { validateEmail } from '@/helpers/methods';
import CheckMark from '@/components/common/filter/check-mark.vue';
import VueSelect from 'vue-select';

Vue.use(VueToast);

export default {
  components: {
    Modal,
    CheckMark,
    VueSelect,
  },
  props: ['controls'],
  data: () => ({
    alert: '',
    login: '',
    password: '',
    email: '',
    copyRights: false,
    availableData: false,
    availableUsers: false,
    selectedLogin: '',
    selectedUser: null,
  }),
  watch: {
    controls: {
      immediate: true,
      handler() {
        if (!this.controls?.length) return;
        const adminUser = this.controls.find(({ id }) => +id === 1);
        this.selectedUser = adminUser;
        this.selectedLogin = adminUser.login;
      },
    },
  },
  methods: {
    setSelectedUser(user) {
      this.selectedUser = user;
    },
    toggleRights() {
      this.copyRights = !this.copyRights;
    },
    toggleData() {
      this.availableData = !this.availableData;
    },
    toggleUsers() {
      this.availableUsers = !this.availableUsers;
    },
    validate() {
      if (validateEmail(this.email)) {
        this.alert = '';
        return true;
      }
      this.alert = this.$t('toast.invalidEmail');
      return false;
    },
    close() {
      this.$emit('close');
    },
    async addUser() {
      if (this.login.length) {
        if (this.validate()) {
          const etalonStatus = this.copyRights ? this.selectedUser.status : null;
          const etalonID = this.copyRights ? this.selectedUser.id : null;
          const isData = this.copyRights && this.availableData;
          const isUsers = this.copyRights && this.availableUsers;

          const result = await addControl(
            this.login,
            this.password,
            this.email,
            etalonStatus,
            etalonID,
            isData,
            isUsers,
          );
          if (result === 'success') {
            this.login = '';
            this.password = '';
            this.email = '';
          }
          this.close();
          this.$emit('update');
        }
      } else {
        Vue.$toast.open({
          message: this.$t('toast.enterLogin'),
          type: 'error',
        });
      }
    },
  },
};
</script>

<style lang="scss" module>
  .input {
    margin: 5px;
    width: 60%;
    border: 1px solid rgba(60, 60, 60, 0.26);
    border-radius: 4px;
    height: 30px;
  }
  .inputContainer {
    font-size: 13px;
    margin: 5px 30px;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
  }
  .inputContainerCheckbox {
    padding-left: 10px;
  }
  .label {
    width: 40%;
  }
  .alert {
    padding: 5px;
    color: $red;
  }

  .titleRight {
    margin: 5px 0;
    padding: 5px 30px;
  }

  .userContainer {
    padding-left: 5px;
  }

  .customCheckBoxContainer {
    .customCheckbox {
      border: 0;
      clip: rect(0 0 0 0);
      height: 1px;
      margin: -1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      width: 1px;
    }

    input:checked + label > svg {
      animation: draw-checkbox ease-in-out 0.2s forwards;
    }

    label:active::after {
      background-color: #fbfbfb;
    }

    label {
      color: black;
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 30px;
      cursor: pointer;
      position: relative;
      display: block;

      &:after {
        content: '';
        height: 14px;
        width: 14px;
        float: left;
        border: 1px solid #9f9f9f;
        border-radius: 4px;
        transition: 0.15s all ease-out;
        background-color: white;
        margin: 9px 0;
      }
    }

    input:checked + label:after {
      background-color: $blue;
      border: 1px solid $blue;
    }

    svg {
      position: absolute;
      left: 4px;
      top: 13px;
      stroke-dasharray: 33;
    }

    @keyframes draw-checkbox {
      0% {
        stroke-dashoffset: 33;
      }

      100% {
        stroke-dashoffset: 0;
      }
    }
  }

  .textContainer {
    white-space: nowrap;
    text-overflow: ellipsis;
    display: inline-block;
    line-height: 21px;
    padding-left: 10px;
    padding-top: 6px;
  }

  .selectContainer {
    display: flex;
    align-items: center;
    gap: 5%;
    font-size: 14px;
    width: 100%;
    margin: 15px;
  }

  .select {
    width: 60%;
  }

  .select .vs__dropdown-menu {
    z-index: 1000;
  }

  .disabledContainer {
    pointer-events: none;
    opacity: 0.4;
  }
</style>
